/* eslint-disable react/prop-types */
import React, {
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { toast } from 'react-toastify';

import {
  Table,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  TableBody,
  TablePagination,
  Paper,
  TableSortLabel,
  LinearProgress,
} from '@material-ui/core';
import { Search } from '@material-ui/icons';
import {
  assign,
  concat,
  isEmpty,
  find,
  trim,
  isEqual,
  isArray,
  isFunction,
} from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { Input, Select, DateTime, Autocomplete } from '~/components/Form';
import { convertType, filterSerialize, t, number } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function SearchTable({
  url,
  header,
  read,
  nowrap,
  onRow,
  maxHeight,
  query,
  sending,
  close,
  changeField,
}) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [isHeader, setIsHeader] = useState([]);
  const [isActive, setIsActive] = useState({});
  const [formData, setFormData] = useState({});
  const [order, setOrder] = useState({ orderfield: '', ordination: 'asc' });
  const [windowHeight, setWindowHeight] = useState(
    `${window.innerHeight - 400}px`
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 425);

  const formRef = useRef(null);
  const containerRef = useRef(null);

  const isQuery = useMemo(
    () =>
      assign(
        {},
        ...isHeader
          .filter(
            (head) =>
              !!head.filter &&
              (!!head.filter.defaultValue || head.filter.type === 'select')
          )
          .map((head) => ({ [head.id]: head.filter.defaultValue || ' ' }))
      ),
    [isHeader]
  );

  const handleManualSubmitForm = useCallback((key) => {
    if (key === 'Enter') formRef.current.submitForm();
  }, []);

  const handleSort = useCallback((orderfield) => {
    setOrder((state) =>
      state.orderfield === orderfield && state.ordination === 'asc'
        ? { orderfield, ordination: 'desc' }
        : { orderfield, ordination: 'asc' }
    );

    onRow({});
  }, []);

  const handleRenderInputToFilter = useCallback(
    (name, type, label, options, icon, autocomplete) => {
      switch (type) {
        case 'text':
        case 'number':
        default:
          return (
            <Input
              name={name}
              type={type || 'text'}
              label={label}
              variant={icon ? 'standard' : 'outlined'}
              InputProps={
                icon
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }
                  : {}
              }
              onKeyPress={({ key }) => handleManualSubmitForm(key)}
            />
          );
        case 'select':
          return (
            <Select
              name={name}
              options={concat(
                [{ value: ' ', label: 'Todos' }],
                options.map((op) => ({ label: op.value, value: op.label }))
              )}
              variant={icon ? 'standard' : 'outlined'}
              label={label}
              InputProps={
                icon
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }
                  : {}
              }
              getValue={() =>
                isMobile ? undefined : handleManualSubmitForm('Enter')
              }
            />
          );
        case 'datetime':
        case 'initialdatetime':
        case 'finaldatetime':
          return (
            <DateTime
              time
              name={name}
              inputVariant={icon ? 'standard' : 'outlined'}
              label={label}
              onKeyPress={({ key }) => handleManualSubmitForm(key)}
            />
          );
        case 'date':
          return (
            <DateTime
              name={name}
              inputVariant={icon ? 'standard' : 'outlined'}
              label={label}
              onKeyPress={({ key }) => handleManualSubmitForm(key)}
            />
          );
        case 'autocomplete':
          return (
            <Autocomplete
              name={name}
              label={autocomplete.label}
              url={autocomplete.url}
              unique={autocomplete.unique}
              title={label}
              variant={icon ? 'standard' : 'outlined'}
              onSerialize={autocomplete.serialize}
              getValue={() =>
                isMobile ? undefined : handleManualSubmitForm('Enter')
              }
            />
          );
      }
    },
    [isMobile]
  );

  const renderCellsHeader = useMemo(
    () =>
      isHeader.map((head, key) => (
        <TableCell
          key={`table-heading-${key}`}
          align={head.align || 'left'}
          padding="default"
          variant="head"
          sortDirection={order.id === head.id ? order.direction : false}
        >
          <TableSortLabel
            active={order.orderfield === head.id}
            direction={order.orderfield === head.id ? order.ordination : 'asc'}
            onClick={() => handleSort(head.id)}
          >
            <p style={{ whiteSpace: 'nowrap' }}>{head.label}</p>
          </TableSortLabel>
          {head.filter &&
            handleRenderInputToFilter(
              head.id,
              head.filter.type,
              '',
              head.filter.options,
              true,
              head.filter.autocomplete
            )}
        </TableCell>
      )),
    [isHeader, order, isMobile]
  );

  const handleResponseAPI = useCallback(
    async (isQuery) => {
      try {
        let dataQuery = isQuery;

        setLoading(true);

        if (!isEmpty(query.wherefield)) {
          if (isEmpty(dataQuery)) dataQuery = query;
          else if (isEmpty(dataQuery.wherefield))
            dataQuery = assign(dataQuery, query);
          else {
            dataQuery = {
              ...dataQuery,
              wherefield: concat(dataQuery.wherefield, query.wherefield),
              valuewherefield: concat(
                dataQuery.valuewherefield,
                query.valuewherefield
              ),
              typewherefield: concat(
                dataQuery.typewherefield,
                query.typewherefield
              ),
            };
          }
        }

        const { data } = await api.get(url, {
          params: dataQuery,
          paramsSerializer: (param) => qs.stringify(param),
        });

        setIsActive({});
        setItems(data.items);
        setPage(data.page);
        setPageSize(data.pageSize);
        setTotalCount(data.totalCount);

        if (isMobile) window.scrollTo(0, 0);
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      } finally {
        setLoading(false);
      }
    },
    [read, isMobile, query, url]
  );

  const handleSubmit = useCallback(
    (data) => {
      const isData = assign(formData, data);

      const inArray = isHeader
        .filter((head) => !!head.filter)
        .map((head) => ({ id: head.id, type: head.filter.type }));

      const isQuery = filterSerialize(
        Object.keys(isData).map((head) => ({
          name: head,
          type: convertType(find(inArray, (arr) => arr.id === head).type),
          value: trim(isData[head]),
        }))
      );

      handleResponseAPI({
        page: 1,
        pagesize: 10,
        ...(isEmpty(isQuery.wherefield) ? {} : isQuery),
        ...(isEmpty(order.orderfield) ? {} : order),
      });

      setFormData(isData);
      onRow({});
    },
    [isHeader, formData, order]
  );

  const handleChangePage = useCallback(
    (page) => {
      const inArray = isHeader
        .filter((head) => !!head.filter)
        .map((head) => ({ id: head.id, type: head.filter.type }));

      const query = filterSerialize(
        Object.keys(formData).map((head) => ({
          name: head,
          type: convertType(find(inArray, (arr) => arr.id === head).type),
          value: trim(formData[head]),
        }))
      );

      handleResponseAPI({
        pagesize: pageSize,
        page,
        ...(isEmpty(query.wherefield) ? {} : query),
        ...(isEmpty(order.orderfield) ? {} : order),
      });
      onRow({});
    },
    [formData, isHeader, order, pageSize]
  );

  const handleChangeRowsPerPage = useCallback(
    (pagesize) => {
      const inArray = isHeader
        .filter((head) => !!head.filter)
        .map((head) => ({ id: head.id, type: head.filter.type }));

      const query = filterSerialize(
        Object.keys(formData).map((head) => ({
          name: head,
          type: convertType(find(inArray, (arr) => arr.id === head).type),
          value: trim(formData[head]),
        }))
      );

      handleResponseAPI({
        page: 1,
        pagesize,
        ...(isEmpty(query.wherefield) ? {} : query),
        ...(isEmpty(order.orderfield) ? {} : order),
      });
      onRow({});
    },
    [formData, isHeader, order]
  );

  const handleClickRow = useCallback((data) => {
    onRow(data);
    setIsActive(data);
  }, []);

  const handleSubmitItem = useCallback(() => {
    changeField(isActive);
    close();
  }, [changeField, isActive, close]);

  useEffect(() => {
    if (!isEqual(header, isHeader)) {
      setIsHeader(header);
    }
  }, [header, isHeader]);

  useEffect(() => {
    if (!isEmpty(isQuery)) {
      handleManualSubmitForm('Enter');
    } else {
      handleResponseAPI({});
    }
  }, [isQuery]);

  useEffect(() => {
    if (!isEmpty(order.orderfield)) {
      const inArray = isHeader
        .filter((head) => !!head.filter)
        .map((head) => ({ id: head.id, type: head.filter.type }));

      const query = filterSerialize(
        Object.keys(formData).map((head) => ({
          name: head,
          type: convertType(find(inArray, (arr) => arr.id === head).type),
          value: trim(formData[head]),
        }))
      );

      handleResponseAPI({
        page: 1,
        pagesize: pageSize,
        ...(isEmpty(query.wherefield) ? {} : query),
        ...(isEmpty(order.orderfield) ? {} : order),
      });
    }
  }, [order, isHeader, formData, pageSize]);

  useEffect(() => {
    if (maxHeight) setWindowHeight(maxHeight);
  }, [maxHeight]);

  useEffect(() => {
    if (sending && !isEmpty(isActive)) handleSubmitItem();
  }, [sending, isActive]);

  window.addEventListener('resize', () => {
    // check is mobile
    if (window.innerWidth <= 500) setIsMobile(true);
    else setIsMobile(false);

    // change height
    if (!maxHeight) {
      setWindowHeight(`${window.innerHeight - 400}px`);
    }
  });

  return (
    <S.Wrapper
      ref={formRef}
      onSubmit={handleSubmit}
      initialData={assign(isQuery, formData)}
    >
      {loading && <LinearProgress />}
      <Paper elevation={2}>
        <Paper variant="outlined" style={{ border: 0 }}>
          <S.WrapperContainer ref={containerRef} maxHeight={windowHeight}>
            <Table stickyHeader aria-labelledby="unidataTable">
              <TableHead>
                <TableRow>{renderCellsHeader}</TableRow>
              </TableHead>
              <TableBody>
                {items.map((data, index) => (
                  <TableRow
                    key={`cell-body-${index}`}
                    onClick={() => handleClickRow(data)}
                    selected={isEqual(data, isActive)}
                    onDoubleClick={() => handleSubmitItem()}
                  >
                    {isHeader.map(({ id, formatted, styled, align }, key) => (
                      <S.Cell
                        key={`cell-body-${index}-${key}`}
                        nowrap={Number(nowrap)}
                        style={isFunction(styled) ? styled(data[id]) : {}}
                        align={align || 'left'}
                      >
                        {formatted ? formatted(data[id]) : data[id]}
                      </S.Cell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </S.WrapperContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={pageSize}
            page={page - 1}
            labelRowsPerPage={t('s.geral.linhasporpagina')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${number(to, 0)} ${t('s.geral.de')} ${number(count, 0)}`
            }
            onChangePage={(e, page) => handleChangePage(page + 1)}
            onChangeRowsPerPage={(e) => handleChangeRowsPerPage(e.target.value)}
          />
        </Paper>
      </Paper>
    </S.Wrapper>
  );
}

SearchTable.propTypes = {
  url: PropTypes.string.isRequired,
  nowrap: PropTypes.bool,
  header: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      formatted: PropTypes.func,
      styled: PropTypes.func,
      onDelete: PropTypes.func,
      onUpdate: PropTypes.func,
      align: PropTypes.string,
      filter: PropTypes.shape({
        type: PropTypes.string.isRequired,
        defaultValue: PropTypes.string,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
          })
        ),
        autocomplete: PropTypes.shape({
          label: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          unique: PropTypes.string.isRequired,
        }),
      }),
    })
  ).isRequired,
  keys: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]).isRequired,
  maxHeight: PropTypes.string,
  query: PropTypes.objectOf(
    PropTypes.shape({
      wherefield: PropTypes.arrayOf(PropTypes.string).isRequired,
      valuewherefield: PropTypes.arrayOf(PropTypes.string).isRequired,
      typewherefield: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ),
  sending: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  changeField: PropTypes.func.isRequired,
};

SearchTable.defaultProps = {
  dense: false,
  nowrap: false,
  custom: [],
  actions: [],
  title: '',
  create: true,
  update: true,
  delete: true,
  toolbar: undefined,
  onRow: () => undefined,
  form: undefined,
  maxWidth: 'sm',
  find: () => undefined,
  remove: () => undefined,
  fixed: false,
  maxHeight: '',
  pdf: false,
  xls: false,
  onExport: () => undefined,
  labelButtonConfirm: 's.geral.botaoconfirmar',
  labelButtonCancell: 's.geral.botaocancelar',
  query: {
    wherefield: [],
    valuewherefield: [],
    typewherefield: [],
  },
};
